/**
 * EmailReauthBanner - Persistent warning banner shown when the email
 * connection has expired and requires re-authentication. (KAN-60)
 *
 * Visible to all users of the affected client. Non-dismissable.
 * Includes a "Go to Settings" link for admin users.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { oauthService } from '../../services/api/oauthService';
import { logger } from '../../utils/logger';
import './EmailReauthBanner.css';

const EmailReauthBanner: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Only check for client org users
    if (!user?.organization || user.organization.type !== 'client') return;

    const checkStatus = async () => {
      try {
        const response = await oauthService.getStatus();
        if (response.success && response.data) {
          const { connection, config } = response.data;
          // Show banner if method is connected_account but status requires reauth
          setShowBanner(
            config.method === 'connected_account' &&
            (connection.status === 'requires_reauth' || connection.status === 'error')
          );
        }
      } catch (error) {
        logger.error('Failed to check email connection status for banner:', error);
      }
    };

    checkStatus();
  }, [user]);

  if (!showBanner) return null;

  const isAdmin = user?.role === 'client_admin';

  return (
    <div className="email-reauth-banner">
      <span className="email-reauth-banner-message">
        {t('settings.emailReauthBanner.message')}
      </span>
      {isAdmin && (
        <Link to="/admin" className="email-reauth-banner-link">
          {t('settings.emailReauthBanner.goToSettings')}
        </Link>
      )}
    </div>
  );
};

export default EmailReauthBanner;
